import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || '/account';
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(form.email, form.password);
      toast.success(`Welcome back, ${result.name || 'rally'}!`);
      if (result.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate(redirectTo);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      <section className="pt-32 pb-16 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto bg-[#141414] border border-[#2A2A2A] p-8"
        >
          <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] tracking-wider mb-2 text-center">
            WELCOME BACK.
          </h1>
          <p className="text-[#A19D98] text-sm text-center mb-6">Log in to your rally account.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[#A19D98] text-sm mb-2">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="login-email-input"
                className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
              />
            </div>
            <div>
              <label className="block text-[#A19D98] text-sm mb-2">Password *</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                data-testid="login-password-input"
                className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Log In'}
            </button>
            <p className="text-[#A19D98] text-sm text-center">
              Don't have an account?{' '}
              <Link to="/signup" state={{ redirectTo }} className="text-[#E8651A] hover:underline">
                Create one
              </Link>
            </p>
          </form>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
};

export default Login;
